"use client";
import React, { useRef } from "react";
import {
  Save,
  X,
  Tag,
  Lock,
  Unlock,
  BookOpen,
  Upload,
  ImageIcon,
} from "lucide-react";
import useCourseForm from "../hooks/useCourseForm";

const UploadCourseForm = ({
  onSave,
  onCancel,
  initialData,
  isEditing = false,
}) => {
  const fileInputRef = useRef(null);
  const {
    formData,
    setFormData,
    handleInputChange,
    categories,
    levels,
    modes,
    dragActive,
    currentTag,
    setCurrentTag,
    handleDrag,
    handleDrop,
    handleImageUpload,
    addTag,
    removeTag,
    handleSubmit,
    totalDuration,
    programTypeList,
  } = useCourseForm(initialData, isEditing, onSave);

  return (
    <div className="w-full bg-[#0d1b2e] mx-auto">
      {/* Form Content */}
      <div className="max-w-7xl mx-auto ">
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Course Basic Info */}
              <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
                <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-100 mb-6">
                  <BookOpen size={24} className="text-cyan-400" />
                  Course Information
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Course Name
                    </label>
                    <input
                      type="text"
                      value={formData.courseName}
                      onChange={(e) =>
                        handleInputChange("courseName", e.target.value)
                      }
                      placeholder="Enter course name..."
                      className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Description (
                      <span className="text-yellow-400">minimum 110 word</span>)
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange("description", e.target.value)
                      }
                      placeholder="Describe what students will learn..."
                      minLength={90}
                      rows={4}
                      className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Sample / Intro YouTube Video Link{" "}
                      <span className="text-slate-500 text-xs">
                        (shown to students until real lectures are uploaded)
                      </span>
                    </label>
                    <input
                      type="url"
                      value={formData.sampleVideoUrl}
                      onChange={(e) =>
                        handleInputChange("sampleVideoUrl", e.target.value)
                      }
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-2 border border-[#1d3350] rounded-lg px-4 py-3">
                    <input
                      type="checkbox"
                      id="isFeaturedOnHome"
                      checked={!!formData.isFeaturedOnHome}
                      onChange={(e) =>
                        handleInputChange("isFeaturedOnHome", e.target.checked)
                      }
                      className="w-4 h-4"
                    />
                    <label
                      htmlFor="isFeaturedOnHome"
                      className="text-sm font-medium text-slate-300 cursor-pointer"
                    >
                      Show on Homepage{" "}
                      <span className="text-slate-500 text-xs">
                        (only 5 courses total show there — tick the ones you
                        want featured)
                      </span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Category
                      </label>
                      <input
                        onChange={(e) =>
                          handleInputChange("category", e.target.value)
                        }
                        value={formData.category}
                        required
                        className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        list="categoryData"
                        placeholder="Enter category"
                      />
                      <datalist
                        
                        id="categoryData"
                        className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select category</option>
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Program Type
                      </label>
                      <select
                        value={formData.programType}
                        onChange={(e) =>
                          handleInputChange("programType", e.target.value)
                        }
                        className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select program type</option>
                        {programTypeList.map((programType, idx) => (
                          <option key={idx} value={programType}>
                            {programType
                              .replace(/-/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Level
                      </label>
                      <select
                        value={formData.level}
                        onChange={(e) =>
                          handleInputChange("level", e.target.value)
                        }
                        className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {levels.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        mode (online/offline)
                      </label>
                      <select
                        value={formData.mode}
                        onChange={(e) =>
                          handleInputChange("mode", e.target.value)
                        }
                        className="w-full px-4 py-3 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {modes.map((mode) => (
                          <option key={mode} value={mode}>
                            {mode}
                          </option>
                        ))}
                      </select>
                    </div>
                     <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Slug (URL-friendly identifier)
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      placeholder = "Enter slug (e.g., 'react-for-beginners')"
                      onChange={(e) =>
                        handleInputChange("slug", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-100 mb-4">
                  <Tag size={20} className="text-cyan-400" />
                  Tags
                </h3>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addTag())
                    }
                    placeholder="Add a tag..."
                    className="flex-1 px-4 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 bg-blue-500/15 text-blue-300 px-3 py-1 rounded-full text-sm"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="text-blue-400 hover:text-blue-200"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
               
            {/* Sidebar */}

            {/* poster upload section  */}
            <div className="space-y-6">
              <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
                <label className="flex items-center gap-2 text-lg font-semibold text-slate-100 mb-4">
                  <ImageIcon size={20} className="text-cyan-400" />
                  Blog Poster
                </label>

                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-300 ${
                    dragActive
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-[#1d3350] hover:border-slate-500"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {formData.poster ? (
                    <div className="space-y-4">
                      <img
                        src={formData.poster.url}
                        alt="Preview"
                        className="w-full h-32 object-cover rounded-lg"
                        loading="lazy"
                        decoding="async"
                      />
                      <p className="text-sm text-slate-400">
                        {formData.poster.name}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((prev) => ({ ...prev, poster: null }))
                        }
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload size={48} className="text-slate-500 mx-auto" />
                      <div>
                        <p className="text-slate-400 mb-2">Drop image here or</p>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-cyan-400 hover:text-cyan-300 font-medium"
                        >
                          Browse Files
                        </button>
                      </div>
                      <p className="text-xs text-slate-400">
                        Supports JPG, PNG, GIF up to 10MB
                      </p>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              {/* Course Settings */}
              <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">
                  Course Settings
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Instructor
                    </label>
                    <input
                      type="text"
                      value={formData.instructor}
                      onChange={(e) =>
                        handleInputChange("instructor", e.target.value)
                      }
                      className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Duration (hours)
                    </label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) =>
                        handleInputChange("duration", e.target.value)
                      }
                      placeholder="Total course duration"
                      className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {totalDuration > 0 && (
                      <p className="text-xs text-slate-400 mt-1">
                        Calculated from lectures:{" "}
                        {Math.round((totalDuration / 60) * 100) / 100} hours
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between py-3 border-b border-[#1d3350]">
                    <div>
                      <label className="text-sm font-medium text-slate-100">
                        Paid Course
                      </label>
                      <p className="text-xs text-slate-400">
                        Enable pricing for this course
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleInputChange("isPaid", !formData.isPaid)
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.isPaid ? "bg-blue-600" : "bg-slate-700"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.isPaid ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>

                  {formData.isPaid && (
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Price (₹)
                      </label>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) =>
                          handleInputChange("price", e.target.value)
                        }
                        placeholder="99.99"
                        className="w-full px-3 py-2 bg-[#0d1220] text-slate-100 placeholder:text-slate-500 border border-[#1d3350] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <label className="text-sm font-medium text-slate-100">
                        Course Locked
                      </label>
                      <p className="text-xs text-slate-400">
                        Restrict access to enrolled students
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        handleInputChange("isLocked", !formData.isLocked)
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        formData.isLocked ? "bg-red-600" : "bg-green-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          formData.isLocked ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Course Stats */}
              <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
                <h3 className="text-lg font-semibold text-slate-100 mb-4">
                  Course Overview
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">
                      Total Lectures
                    </span>
                    <span className="font-medium">
                      {formData.lectures.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">
                      Total Duration
                    </span>
                    <span className="font-medium">{totalDuration} minutes</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Course Type</span>
                    <span
                      className={`text-sm font-medium ${
                        formData.isPaid ? "text-green-400" : "text-blue-400"
                      }`}
                    >
                      {formData.isPaid ? "Paid" : "Free"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Access</span>
                    <span
                      className={`flex items-center gap-1 text-sm font-medium ${
                        formData.isLocked ? "text-red-400" : "text-green-400"
                      }`}
                    >
                      {formData.isLocked ? (
                        <Lock size={14} />
                      ) : (
                        <Unlock size={14} />
                      )}
                      {formData.isLocked ? "Locked" : "Open"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-[#0f1c2e] rounded-xl p-6 border border-[#1d3350]">
                <div className="flex flex-col gap-3 sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 sm:w-auto"
                  >
                    <Save size={20} />
                    {isEditing ? "Update Course" : "Create Course"}
                  </button>
                  {onCancel && (
                    <button
                      type="button"
                      onClick={onCancel}
                      className="w-full py-3 px-6 rounded-lg border border-[#1d3350] text-slate-300 font-medium hover:bg-[#0d1b2e] sm:w-auto"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Lectures Section */}
        {/* <LectureForm courseId={initialData._id} courseLectures={courseLectures} setCourseLectures={setCourseLectures}/> */}
      </div>
    </div>
  );
};

export default UploadCourseForm;